import React, { useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Button, ButtonBase, Container, Dialog, Typography } from "@material-ui/core";
import {
  ArrowForward, BarChart, CheckCircle, Chat, EmojiEvents, Equalizer, GetApp, Group, Layers, Public, SportsEsports, SportsKabaddi,
  SportsMma, Timeline, Videocam,
} from '@material-ui/icons';
import { useHistory } from "react-router-dom";
import FortIcon from "components/Icons/FortIcon";
import { downloadApp } from "utilities/download";
import heroArt from 'assets/images/backgrounds/khitaigreatwall.jpg';
import matchmakingArt from 'assets/images/backgrounds/karakorum.jpg';
import modesArt from 'assets/images/backgrounds/ardashirfort.jpg';
import overlayArt from 'assets/images/backgrounds/paikangforest.jpg';
import summaryArt from 'assets/images/backgrounds/commonsdistrict.jpg';
import duelArt from 'assets/images/backgrounds/chaosroom.jpg';
import pveArt from 'assets/images/backgrounds/jadecitadel.jpg';
import profileArt from 'assets/images/backgrounds/khemi.jpg';

const ACCENT = '#42a5f5';

const useStyles = makeStyles((theme) => ({
  // ---- hero
  hero: {
    position: 'relative',
    overflow: 'hidden',
    backgroundSize: 'cover',
    backgroundPosition: 'center 35%',
  },
  heroShade: {
    position: 'absolute',
    top: 0, right: 0, bottom: 0, left: 0,
    background: 'linear-gradient(90deg, rgba(14,15,19,0.96) 0%, rgba(14,15,19,0.82) 45%, rgba(14,15,19,0.45) 100%), linear-gradient(0deg, #282b32 0%, transparent 30%)',
  },
  heroInner: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.05fr)',
    alignItems: 'center',
    gap: `${theme.spacing(6)}px`,
    padding: theme.spacing(11, 0, 12),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: `${theme.spacing(5)}px`,
      padding: theme.spacing(6, 0, 8),
    },
  },
  eyebrow: {
    display: 'inline-block',
    padding: theme.spacing(0.5, 1.5),
    marginBottom: theme.spacing(2.5),
    borderRadius: 20,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#bbdefb',
    background: 'rgba(66, 165, 245, 0.14)',
    border: '1px solid rgba(66, 165, 245, 0.35)',
  },
  heroTitle: {
    fontSize: '3.4rem',
    fontWeight: 900,
    lineHeight: 1.05,
    letterSpacing: '-0.01em',
    [theme.breakpoints.down('xs')]: {
      fontSize: '2.4rem',
    },
  },
  heroAccent: {
    background: 'linear-gradient(90deg, #64b5f6, #90caf9)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  heroText: {
    marginTop: theme.spacing(2.5),
    fontSize: '1.15rem',
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.78)',
    maxWidth: 560,
  },
  ctas: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: `${theme.spacing(1.5)}px`,
    marginTop: theme.spacing(4),
  },
  primaryCta: {
    padding: theme.spacing(1.5, 3.5),
    fontSize: '1.05rem',
    fontWeight: 800,
    textTransform: 'none',
    borderRadius: 10,
    background: 'linear-gradient(135deg, #1e88e5, #1565c0)',
    boxShadow: '0 8px 24px rgba(30, 136, 229, 0.45)',
    '&:hover': {
      background: 'linear-gradient(135deg, #2196f3, #1976d2)',
    },
  },
  secondaryCta: {
    padding: theme.spacing(1.5, 3),
    fontSize: '1.05rem',
    fontWeight: 700,
    textTransform: 'none',
    borderRadius: 10,
    borderColor: 'rgba(255,255,255,0.3)',
  },
  heroNote: {
    marginTop: theme.spacing(2),
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.55)',
  },
  // ---- mode strip
  strip: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
    gap: `${theme.spacing(2)}px`,
    marginTop: theme.spacing(-6),
    position: 'relative',
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
    },
  },
  stripItem: {
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    background: 'rgba(23, 24, 29, 0.96)',
    border: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 10px 30px rgba(0,0,0,0.35)',
  },
  stripValue: {
    fontSize: '1.05rem',
    fontWeight: 800,
  },
  stripLabel: {
    marginTop: 2,
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
  },
  // ---- sections
  section: {
    padding: theme.spacing(10, 0),
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(6, 0),
    },
  },
  sectionAlt: {
    background: 'linear-gradient(180deg, rgba(0,0,0,0.18), rgba(0,0,0,0.05))',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    borderBottom: '1px solid rgba(255,255,255,0.05)',
  },
  sectionHead: {
    textAlign: 'center',
    maxWidth: 680,
    margin: '0 auto',
    marginBottom: theme.spacing(8),
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(5),
    },
  },
  kicker: {
    fontSize: '0.78rem',
    fontWeight: 800,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: ACCENT,
  },
  sectionTitle: {
    marginTop: theme.spacing(1),
    fontSize: '2.3rem',
    fontWeight: 900,
    lineHeight: 1.15,
    [theme.breakpoints.down('xs')]: {
      fontSize: '1.8rem',
    },
  },
  sectionText: {
    marginTop: theme.spacing(1.5),
    fontSize: '1.05rem',
    lineHeight: 1.6,
    color: 'rgba(255,255,255,0.7)',
  },
  // ---- feature rows
  feature: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 0.9fr) minmax(0, 1.1fr)',
    alignItems: 'center',
    gap: `${theme.spacing(7)}px`,
    '& + &': {
      marginTop: theme.spacing(12),
    },
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: 'minmax(0, 1fr)',
      gap: `${theme.spacing(3)}px`,
      '& + &': {
        marginTop: theme.spacing(8),
      },
    },
  },
  featureFlip: {
    '& > :first-child': {
      order: 2,
    },
    [theme.breakpoints.down('sm')]: {
      '& > :first-child': {
        order: 0,
      },
    },
  },
  featureIcon: {
    width: 48,
    height: 48,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 12,
    color: ACCENT,
    background: 'rgba(66, 165, 245, 0.12)',
    border: '1px solid rgba(66, 165, 245, 0.25)',
    marginBottom: theme.spacing(2),
  },
  featureTitle: {
    fontSize: '1.75rem',
    fontWeight: 800,
    lineHeight: 1.2,
  },
  featureText: {
    marginTop: theme.spacing(1.5),
    fontSize: '1.02rem',
    lineHeight: 1.65,
    color: 'rgba(255,255,255,0.72)',
  },
  points: {
    listStyle: 'none',
    padding: 0,
    margin: theme.spacing(2.5, 0, 0),
    '& li': {
      display: 'grid',
      gridTemplateColumns: '22px minmax(0, 1fr)',
      gap: `${theme.spacing(1)}px`,
      alignItems: 'start',
      marginBottom: theme.spacing(1.25),
      lineHeight: 1.5,
      color: 'rgba(255,255,255,0.85)',
    },
    '& svg': {
      fontSize: '1.15rem',
      marginTop: 2,
      color: '#66bb6a',
    },
  },
  // ---- screenshot frame
  frame: {
    display: 'block',
    width: '100%',
    borderRadius: 12,
    overflow: 'hidden',
    background: '#15161a',
    border: '1px solid rgba(255,255,255,0.1)',
    boxShadow: '0 24px 60px rgba(0,0,0,0.55), 0 0 0 1px rgba(0,0,0,0.4)',
    textAlign: 'left',
  },
  frameBar: {
    display: 'flex',
    alignItems: 'center',
    height: 30,
    padding: theme.spacing(0, 1.5),
    background: '#1d1f25',
    borderBottom: '1px solid rgba(255,255,255,0.06)',
    '& > span': {
      width: 10,
      height: 10,
      borderRadius: '50%',
      marginRight: 6,
      background: 'rgba(255,255,255,0.18)',
    },
  },
  frameTitle: {
    marginLeft: theme.spacing(1),
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.5)',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  shot: {
    display: 'block',
    width: '100%',
    height: 'auto',
  },
  art: {
    position: 'relative',
    aspectRatio: '16 / 9',
    minHeight: 200,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    display: 'grid',
    placeItems: 'center',
  },
  artShade: {
    position: 'absolute',
    top: 0, right: 0, bottom: 0, left: 0,
    background: 'radial-gradient(circle at center, rgba(14,15,19,0.35), rgba(14,15,19,0.85))',
  },
  artIcon: {
    position: 'relative',
    width: 84,
    height: 84,
    display: 'grid',
    placeItems: 'center',
    borderRadius: 20,
    color: '#fff',
    background: 'rgba(66, 165, 245, 0.25)',
    border: '1px solid rgba(144, 202, 249, 0.5)',
    backdropFilter: 'blur(6px)',
    '& svg': {
      fontSize: 44,
    },
  },
  lightbox: {
    display: 'block',
    maxWidth: '100%',
    maxHeight: '90vh',
  },
  // ---- smaller features
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
    gap: `${theme.spacing(2.5)}px`,
  },
  card: {
    padding: theme.spacing(3),
    borderRadius: 12,
    background: 'rgba(23, 24, 29, 0.9)',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'border-color 0.15s, transform 0.15s',
    '&:hover': {
      borderColor: 'rgba(66, 165, 245, 0.45)',
      transform: 'translateY(-2px)',
    },
  },
  cardTitle: {
    marginTop: theme.spacing(0.5),
    fontSize: '1.1rem',
    fontWeight: 800,
  },
  cardText: {
    marginTop: theme.spacing(0.75),
    fontSize: '0.95rem',
    lineHeight: 1.55,
    color: 'rgba(255,255,255,0.68)',
  },
  // ---- explore links
  explore: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: `${theme.spacing(2)}px`,
  },
  exploreCard: {
    display: 'grid',
    gridTemplateColumns: 'auto minmax(0, 1fr) auto',
    alignItems: 'center',
    gap: `${theme.spacing(1.5)}px`,
    width: '100%',
    padding: theme.spacing(2, 2.25),
    borderRadius: 12,
    textAlign: 'left',
    background: 'rgba(23, 24, 29, 0.9)',
    border: '1px solid rgba(255,255,255,0.08)',
    transition: 'border-color 0.15s, background 0.15s',
    '&:hover': {
      borderColor: 'rgba(66, 165, 245, 0.45)',
      background: 'rgba(30, 32, 38, 0.95)',
    },
    '& > svg:first-child': {
      color: ACCENT,
    },
    '& > svg:last-child': {
      opacity: 0.5,
      fontSize: '1.1rem',
    },
  },
  // ---- closing call to action
  closing: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: 18,
    padding: theme.spacing(7, 4),
    textAlign: 'center',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    border: '1px solid rgba(255,255,255,0.1)',
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(5, 2.5),
    },
  },
  closingShade: {
    position: 'absolute',
    top: 0, right: 0, bottom: 0, left: 0,
    background: 'linear-gradient(135deg, rgba(13, 71, 161, 0.88), rgba(14, 15, 19, 0.9))',
  },
  footer: {
    padding: theme.spacing(4, 0),
    borderTop: '1px solid rgba(255,255,255,0.06)',
    color: 'rgba(255,255,255,0.45)',
    fontSize: '0.85rem',
    textAlign: 'center',
  },
}));

/**
 * A screenshot of the app in a window frame; click to enlarge. Screenshots live in public/images/screenshots;
 * until one has been added the frame shows the feature's game art instead.
 */
const Screenshot: React.FC<{name: string, title: string, art: string, icon: React.ReactNode}> = ({name, title, art, icon}) => {
  const classes = useStyles();
  const [missing, setMissing] = useState(false);
  const [open, setOpen] = useState(false);
  const src = `/images/screenshots/${name}.png`;
  return (
    <React.Fragment>
      <ButtonBase className={classes.frame} disabled={missing} onClick={() => setOpen(true)} aria-label={missing ? undefined : `Enlarge: ${title}`}>
        <div className={classes.frameBar}>
          <span /><span /><span />
          <div className={classes.frameTitle}>PvP Levelup · {title}</div>
        </div>
        {missing
          ? <div className={classes.art} style={{backgroundImage: `url(${art})`}}>
              <div className={classes.artShade} />
              <div className={classes.artIcon}>{icon}</div>
            </div>
          : <img className={classes.shot} src={src} alt={title} onError={() => setMissing(true)} />
        }
      </ButtonBase>
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth={false} PaperProps={{style: {background: 'transparent', boxShadow: 'none', margin: 16}}}>
        <img className={classes.lightbox} src={src} alt={title} onClick={() => setOpen(false)} />
      </Dialog>
    </React.Fragment>
  );
}

interface FeatureProps {
  icon: React.ReactNode;
  title: string;
  text: string;
  points: string[];
  screenshot: string;
  art: string;
  flip?: boolean;
}

const Feature: React.FC<FeatureProps> = ({icon, title, text, points, screenshot, art, flip}) => {
  const classes = useStyles();
  return (
    <div className={clsx(classes.feature, flip && classes.featureFlip)}>
      <div>
        <div className={classes.featureIcon}>{icon}</div>
        <Typography component="h3" className={classes.featureTitle}>{title}</Typography>
        <div className={classes.featureText}>{text}</div>
        <ul className={classes.points}>
          {points.map((point) => <li key={point}><CheckCircle />{point}</li>)}
        </ul>
      </div>
      <Screenshot name={screenshot} title={title} art={art} icon={icon} />
    </div>
  );
}

const FEATURES: FeatureProps[] = [
  {
    icon: <EmojiEvents />,
    title: 'Ranked matchmaking',
    text: 'Queue on your own or with a party, and the matchmaker builds the fairest teams it can from everyone waiting. Your MMR, wins and losses are tracked for every character.',
    points: [
      'Party up with up to 3 friends and queue together',
      'Role-aware queue: see how many soldiers, priests, rogues and mages are waiting, and which roles are needed',
      'Play on Fury or Crom',
      'Jump into the Proving Grounds while you wait for a match',
    ],
    screenshot: 'matchmaking',
    art: matchmakingArt,
  },
  {
    icon: <SportsEsports />,
    title: 'Objective game modes',
    text: 'More than a brawl: four game modes with live scoring, played across zones and maps throughout the game world.',
    points: [
      'Team Deathmatch: the team with the most kills wins',
      'King of the Hill: hold the hill as it moves around the map',
      'Conquest: capture and defend three points',
      'Capture the Flag: steal the enemy flag and bring it home',
    ],
    screenshot: 'game',
    art: modesArt,
  },
  {
    icon: <Layers />,
    title: 'In-game overlay',
    text: 'Everything you need while you fight, drawn on top of the game: no alt-tabbing.',
    points: [
      'Live scoreboard and map with your team\'s positions',
      'Objective and teammate indicators',
      'Death breakdown: see exactly what killed you',
      'Damage monitor with DPS and healing meters',
    ],
    screenshot: 'overlay',
    art: overlayArt,
  },
  {
    icon: <Timeline />,
    title: 'Every match, broken down',
    text: 'Every game is recorded and gets a full summary you can come back to: who did what, when the game turned, and why.',
    points: [
      'Scoreboard with kills, deaths, assists, finishing blows, crowd control and healing',
      'Performance rating for every player in every game',
      'Kill advantage and damage graphs over the course of the game',
      'Each death broken down by ability and attacker',
    ],
    screenshot: 'summary',
    art: summaryArt,
  },
  {
    icon: <SportsKabaddi />,
    title: '1v1 duels',
    text: 'Settle it one on one. Ranked and unranked best-of duels with their own MMR and leaderboard.',
    points: [
      'Ranked and unranked duels, best of 1, 3 or more',
      'Round by round breakdown of every duel',
      'A separate 1v1 MMR and leaderboard',
    ],
    screenshot: 'duels',
    art: duelArt,
  },
  {
    icon: <FortIcon />,
    title: 'Dungeons & raids',
    text: 'Your combat log becomes a full report of the run: every pull, every boss, and everyone\'s damage and healing.',
    points: [
      'Pull by pull damage and healing, with class colors',
      'Boss kills, clear times and hard mode detected automatically',
      'Leaderboards for the fastest clears in every dungeon and raid',
      'Group finder to fill your group for a dungeon or a raid',
    ],
    screenshot: 'pve',
    art: pveArt,
  },
  {
    icon: <BarChart />,
    title: 'Profiles & leaderboards',
    text: 'Every character and player has a profile, and the leaderboards show who\'s on top, in ranked, in duels and in dungeons.',
    points: [
      'Win rates by map, and from the blue or red side',
      'How you do with and against everyone you\'ve played with',
      'Full game, duel and dungeon history',
      'Season leaderboards you can filter by class',
    ],
    screenshot: 'profile',
    art: profileArt,
  },
];

const MORE = [
  {icon: <Group />, title: 'Custom lobbies', text: 'Set up your own games: pick the mode, map and team sizes, and invite who you like.'},
  {icon: <Public />, title: 'Open world PvP', text: 'Kills, deaths and fights in the open world are tracked and summarized too.'},
  {icon: <Chat />, title: 'Chat & friends', text: 'Built-in chat before, during and after games, plus friends and party invites.'},
  {icon: <Videocam />, title: 'Match recording', text: 'Record your matches automatically to watch back or share later.'},
];

const EXPLORE = [
  {icon: <BarChart />, label: 'Ranked leaderboard', path: '/leaderboard'},
  {icon: <SportsMma />, label: '1v1 leaderboard', path: '/duelleaderboard'},
  {icon: <Equalizer />, label: 'Dungeon & raid leaderboards', path: '/pve/leaderboard'},
  {icon: <SportsEsports />, label: 'Past games', path: '/game'},
  {icon: <SportsKabaddi />, label: 'Past duels', path: '/duel'},
  {icon: <FortIcon />, label: 'Dungeon & raid runs', path: '/pve/runs'},
];

export const Home: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();

  return (
    <div>
      <header className={classes.hero} style={{backgroundImage: `url(${heroArt})`}}>
        <div className={classes.heroShade} />
        <Container maxWidth="lg" className={classes.heroInner}>
          <div>
            <div className={classes.eyebrow}>Age of Conan · Fury & Crom</div>
            <Typography component="h1" className={classes.heroTitle}>
              Competitive PvP<br />and PvE, <span className={classes.heroAccent}>leveled up.</span>
            </Typography>
            <div className={classes.heroText}>
              Ranked matchmaking, objective game modes, 1v1 duels, dungeon and raid reports, and an in-game overlay,
              with every fight tracked so you can see how you're improving.
            </div>
            <div className={classes.ctas}>
              <Button variant="contained" color="primary" size="large" startIcon={<GetApp />} className={classes.primaryCta} onClick={downloadApp}>
                Download for Windows
              </Button>
              <Button variant="outlined" size="large" endIcon={<ArrowForward />} className={classes.secondaryCta} onClick={() => history.push('/leaderboard')}>
                See the leaderboards
              </Button>
            </div>
            <div className={classes.heroNote}>Desktop app for Windows. Runs alongside the game client.</div>
          </div>
          <Screenshot name="hero" title="Matchmaking" art={matchmakingArt} icon={<EmojiEvents />} />
        </Container>
      </header>

      <Container maxWidth="lg">
        <div className={classes.strip}>
          <div className={classes.stripItem}>
            <div className={classes.stripValue}>4 game modes</div>
            <div className={classes.stripLabel}>TDM, King of the Hill, Conquest, CTF</div>
          </div>
          <div className={classes.stripItem}>
            <div className={classes.stripValue}>Parties of up to 4</div>
            <div className={classes.stripLabel}>Queue with your friends</div>
          </div>
          <div className={classes.stripItem}>
            <div className={classes.stripValue}>Ranked & 1v1</div>
            <div className={classes.stripLabel}>MMR and seasons</div>
          </div>
          <div className={classes.stripItem}>
            <div className={classes.stripValue}>Dungeons & raids</div>
            <div className={classes.stripLabel}>Runs from your combat log</div>
          </div>
        </div>
      </Container>

      <section className={classes.section}>
        <Container maxWidth="lg">
          <div className={classes.sectionHead}>
            <div className={classes.kicker}>What the app does</div>
            <Typography component="h2" className={classes.sectionTitle}>Everything around the fight</Typography>
            <div className={classes.sectionText}>
              PvP Levelup runs next to Age of Conan and turns your games into matchmade matches, live overlays and detailed stats.
            </div>
          </div>
          {FEATURES.map((feature, index) => <Feature key={feature.title} {...feature} flip={index % 2 == 1} />)}
        </Container>
      </section>

      <section className={clsx(classes.section, classes.sectionAlt)}>
        <Container maxWidth="lg">
          <div className={classes.sectionHead}>
            <div className={classes.kicker}>And more</div>
            <Typography component="h2" className={classes.sectionTitle}>Built for the community</Typography>
          </div>
          <div className={classes.grid}>
            {MORE.map((item) => (
              <div key={item.title} className={classes.card}>
                <div className={classes.featureIcon} style={{width: 42, height: 42, marginBottom: 12}}>{item.icon}</div>
                <div className={classes.cardTitle}>{item.title}</div>
                <div className={classes.cardText}>{item.text}</div>
              </div>
            ))}
          </div>
        </Container>
      </section>

      <section className={classes.section}>
        <Container maxWidth="lg">
          <div className={classes.sectionHead}>
            <Typography component="h2" className={classes.sectionTitle}>Browse it on the web</Typography>
            <div className={classes.sectionText}>Leaderboards, match history and every character's profile are open to everyone, right here.</div>
          </div>
          <div className={classes.explore}>
            {EXPLORE.map((item) => (
              <ButtonBase key={item.path} className={classes.exploreCard} onClick={() => history.push(item.path)}>
                {item.icon}
                <span style={{fontWeight: 700}}>{item.label}</span>
                <ArrowForward />
              </ButtonBase>
            ))}
          </div>
        </Container>
      </section>

      <section className={classes.section} style={{paddingTop: 0}}>
        <Container maxWidth="lg">
          <div className={classes.closing} style={{backgroundImage: `url(${pveArt})`}}>
            <div className={classes.closingShade} />
            <div style={{position: 'relative'}}>
              <Typography component="h2" className={classes.sectionTitle} style={{marginTop: 0}}>Ready to queue?</Typography>
              <div className={classes.sectionText} style={{maxWidth: 560, margin: '12px auto 0'}}>
                Download the app, verify your character, and jump into your first match.
              </div>
              <div className={classes.ctas} style={{justifyContent: 'center'}}>
                <Button variant="contained" color="primary" size="large" startIcon={<GetApp />} className={classes.primaryCta} onClick={downloadApp}>
                  Download App
                </Button>
                <Button variant="outlined" size="large" className={classes.secondaryCta} onClick={() => history.push('/rules')}>
                  Read the rules
                </Button>
              </div>
            </div>
          </div>
        </Container>
      </section>

      <footer className={classes.footer}>
        PvP Levelup · A community project for Age of Conan · © {new Date().getFullYear()}
      </footer>
    </div>
  );
}
